import classNames from 'classnames';
import { FC, ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ImageField } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { Button, ButtonTypes, customScrollStyles, TextField, Divider } from '@gem/ui-kit';

import { Avatar, AvatarTypes } from 'APP/components/Avatar/Avatar';
import { minLengthValidation } from 'APP/packages/form/validation/minLengthValidation';
import { requiredValidation } from 'APP/packages/form/validation/requiredValidation';

import {
  IEditProfileFormData,
  MIN_LENGTH_MIDDLE_NAME,
  useEditProfileFormPresenter,
} from './EditProfileForm.presenter';
import styles from './EditProfileForm.styles.m.css';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
} from './EditProfilePopup.constants';
import { PermissionTooltip } from './PermissionTooltip/PermissionTooltip';
import { nickNameValidation } from './validation/nickNameValidation';

interface IEditProfileFormProps {
  submit(data: IEditProfileFormData): Promise<void>;
}

export const EditProfileForm: FC<IEditProfileFormProps> = ({ submit }) => {
  const { t } = useTranslation();
  const presenter = useEditProfileFormPresenter();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isDirty, errors },
  } = useForm<IEditProfileFormData>({
    mode: 'onChange',
    defaultValues: presenter.defaultValues,
  });

  const isValid = !Object.keys(errors).length;

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(submit)}
    >
      <div className={classNames(styles.formContent, customScrollStyles.container)}>
        <div className={styles.formSection}>
          <Controller
            control={control}
            name="avatarUrl"
            render={({ field }): ReactElement => (
              <ImageField
                className={styles.imageField}
                name={field.name}
                value={field.value}
                onLoad={presenter.onImageLoad}
                renderImage={(url): ReactElement => (
                  <Avatar
                    url={url}
                    type={AvatarTypes.Round_40}
                  />
                )}
                emptyImage={
                  <Avatar
                    type={AvatarTypes.Round_40}
                    url=""
                    title={presenter.avatarTitle}
                    backgroundColorIndex={presenter.avatarColorIndex}
                  />
                }
                onChange={field.onChange}
              />
            )}
          />

          <div className={styles.sectionInfo}>
            <div className={styles.sectionName}>{t('profile_public_info')}</div>

            <Controller
              control={control}
              name="displayName"
              rules={{
                ...requiredValidation<IEditProfileFormData>(
                  t('common_required_field_error', { 0: t('profile_displayname_hint') }),
                  { isDisabled: !presenter.allowUserDisplayNameEdit }
                ),
                ...minLengthValidation<IEditProfileFormData>(DISPLAY_NAME_MIN_LENGTH, undefined, {
                  isDisabled: !presenter.allowUserDisplayNameEdit,
                }),
              }}
              render={({ field, fieldState }): ReactElement => (
                <PermissionTooltip disabled={presenter.allowUserDisplayNameEdit}>
                  <TextField
                    className={styles.formField}
                    {...field}
                    maxLength={DISPLAY_NAME_MAX_LENGTH}
                    label={t('profile_displayname_hint')}
                    hasError={fieldState.invalid}
                    isReadOnly={!presenter.allowUserDisplayNameEdit}
                    message={fieldState.error?.message || ' '}
                  />
                </PermissionTooltip>
              )}
            />

            <Controller
              control={control}
              name="nickName"
              rules={{
                ...requiredValidation<IEditProfileFormData>(
                  t('common_required_field_error', { 0: t('profile_nickname_hint') })
                ),
                ...minLengthValidation<IEditProfileFormData>(NICKNAME_MIN_LENGTH),
                ...nickNameValidation<IEditProfileFormData>(
                  t('nickanme_hint'),
                  t('user_profile_nick_name_already_exists_error')
                ),
              }}
              render={({
                field,
                fieldState: { isValidating, invalid, isDirty, error },
              }): ReactElement => (
                <TextField
                  className={styles.formField}
                  {...field}
                  maxLength={NICKNAME_MAX_LENGTH}
                  label={t('profile_nickname_hint')}
                  hasError={!isValidating && invalid}
                  hasSuccess={!isValidating && isDirty}
                  message={presenter.getNicknameFieldText(isValidating, isDirty, error?.message)}
                />
              )}
            />

            <Controller
              control={control}
              name="position"
              render={({ field, fieldState }): ReactElement => (
                <TextField
                  className={styles.formField}
                  {...field}
                  label={t('user_position')}
                  hasError={fieldState.invalid}
                  message={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>

        <Divider theme="style1" />

        <div className={styles.formSection}>
          <div className={styles.sectionName}>{t('profile_personal_info')}</div>

          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }): ReactElement => (
              <PermissionTooltip disabled={presenter.allowUserProfileEdit}>
                <TextField
                  className={styles.formField}
                  {...field}
                  label={t('profile_name_hint')}
                  hasError={fieldState.invalid}
                  isReadOnly={!presenter.allowUserProfileEdit}
                  message={fieldState.error?.message || ' '}
                />
              </PermissionTooltip>
            )}
          />

          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }): ReactElement => (
              <PermissionTooltip disabled={presenter.allowUserProfileEdit}>
                <TextField
                  className={styles.formField}
                  {...field}
                  label={t('profile_firstname_hint')}
                  hasError={fieldState.invalid}
                  isReadOnly={!presenter.allowUserProfileEdit}
                  message={fieldState.error?.message || ' '}
                />
              </PermissionTooltip>
            )}
          />

          <Controller
            control={control}
            name="middleName"
            rules={{
              ...requiredValidation<IEditProfileFormData>(
                t('common_required_field_error', { 0: t('profile_middlename_hint') }),
                {
                  isDisabled: !presenter.requireMiddleName,
                }
              ),
              ...minLengthValidation<IEditProfileFormData>(MIN_LENGTH_MIDDLE_NAME, undefined, {
                isDisabled: !presenter.requireMiddleName,
              }),
            }}
            render={({ field, fieldState }): ReactElement => (
              <PermissionTooltip disabled={presenter.allowUserProfileEdit}>
                <TextField
                  className={styles.formField}
                  {...field}
                  label={t('profile_middlename_hint')}
                  hasError={fieldState.invalid}
                  isReadOnly={!presenter.allowUserProfileEdit}
                  message={fieldState.error?.message}
                />
              </PermissionTooltip>
            )}
          />
        </div>

        <Divider theme="style1" />

        <div className={styles.formSection}>
          <div className={styles.sectionName}>{t('profile_contact_info')}</div>

          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }): ReactElement => (
              <TextField
                className={styles.formField}
                {...field}
                onChange={(e): void => {
                  const phoneNumber = presenter.parsePhoneNumber?.(e.target.value);
                  field.onChange(phoneNumber);
                }}
                label={t('account_phone_number')}
                hasError={fieldState.invalid}
                message={fieldState.error?.message || ' '}
                type="tel"
                autoComplete="tel"
              />
            )}
          />
        </div>
      </div>

      <div className={styles.formActions}>
        <Button
          type={ButtonTypes.Submit}
          theme="primary"
          size="10"
          isLoading={isSubmitting}
          isDisabled={!isValid || isValidating || !isDirty}
        >
          {t('common_save')}
        </Button>
      </div>
    </form>
  );
};
